import React, { useReducer, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import MobileRedirect from './BrokerTeamAnalyzer/components/MobileRedirect';
import Stepper from './BrokerTeamAnalyzer/components/Stepper';
import Intro from './BrokerTeamAnalyzer/components/Intro';
import SlotCard from './BrokerTeamAnalyzer/components/SlotCard';
import PipelineGrid from './BrokerTeamAnalyzer/components/PipelineGrid';
import ResultsTable from './BrokerTeamAnalyzer/components/ResultsTable';
import PerDealMatrix from './BrokerTeamAnalyzer/components/PerDealMatrix';
import DrillDownPanel from './BrokerTeamAnalyzer/components/DrillDownPanel';
import BrandLogoMark from './BrokerTeamAnalyzer/components/BrandLogoMark';
import { FreeToolsNav } from './FreeToolsPage';

import { reducer, initialState } from './BrokerTeamAnalyzer/lib/reducer';
import { computeAnnualResults } from './BrokerTeamAnalyzer/lib/math';

// Scoped CSS for the tool — isolated from the rest of the app.
const TOOL_CSS = `
  .bta-root { font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif; background: #F7F5EE; color: #092825; min-height: 100vh; }
  .bta-root .num-input::-webkit-outer-spin-button,
  .bta-root .num-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .bta-root .num-input { -moz-appearance: textfield; }
  .bta-root .bta-sticky-col { position: sticky; left: 0; background: white; z-index: 1; }
  @keyframes btaSoftBounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
  @keyframes btaSoftPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(47,161,99,0.35); } 50% { box-shadow: 0 0 0 10px rgba(47,161,99,0); } }
  .bta-root .bta-soft-bounce { animation: btaSoftBounce 2.2s ease-in-out infinite; }
  .bta-root .bta-soft-pulse { animation: btaSoftPulse 2.2s ease-in-out infinite; }
`;

function StepCurrent({ state, dispatch, onNext, onBack }) {
  const current = state.slots.find((s) => s.is_current);
  return (
    <div className="max-w-2xl mx-auto" data-testid="bta-step-current">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#092825]">Your current brokerage</h2>
          <p className="text-slate-600">This becomes the baseline every option is compared against.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={state.currentSkipped}
            onChange={(e) => dispatch({ type: 'SKIP_CURRENT', value: e.target.checked })}
            data-testid="bta-skip-current-checkbox"
          />
          I&apos;m new — no current brokerage
        </label>
      </div>

      {!state.currentSkipped && current && (
        <SlotCard
          slot={current}
          isCurrent
          onUpdate={(patch) => dispatch({ type: 'UPDATE_SLOT', id: current.id, patch })}
          onBrokerageChange={(key) => dispatch({ type: 'SET_SLOT_BROKERAGE', id: current.id, key })}
        />
      )}

      {state.currentSkipped && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center text-slate-500">
          No current brokerage — comparison options will show absolute take-home without delta.
        </div>
      )}

      <div className="mt-6 flex justify-between">
        <button
          type="button"
          onClick={onBack}
          data-testid="bta-step-current-back-btn"
          className="text-slate-600 hover:text-slate-900 font-medium"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          data-testid="bta-step-current-next-btn"
          className="bg-[#2FA163] hover:bg-[#286C4E] text-white font-medium px-6 py-2.5 rounded shadow transition-colors"
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

function StepCompare({ state, dispatch, onNext, onBack }) {
  const options = state.slots.filter((s) => !s.is_current);
  return (
    <div data-testid="bta-step-compare">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#092825]">Add brokerages to compare</h2>
        <p className="text-slate-600">Up to 4 options. Each can include a team or not.</p>
      </div>

      <div className={'grid gap-6 ' + (options.length <= 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4')}>
        {options.map((slot) => (
          <SlotCard
            key={slot.id}
            slot={slot}
            onUpdate={(patch) => dispatch({ type: 'UPDATE_SLOT', id: slot.id, patch })}
            onBrokerageChange={(key) => dispatch({ type: 'SET_SLOT_BROKERAGE', id: slot.id, key })}
            onRemove={() => dispatch({ type: 'REMOVE_SLOT', id: slot.id })}
          />
        ))}
      </div>

      {options.length < 4 && (
        <div className="mt-8 flex items-center gap-5">
          <BrandLogoMark className="shrink-0 w-20 h-20 bta-soft-bounce" />
          <button
            type="button"
            onClick={() => dispatch({ type: 'ADD_SLOT' })}
            data-testid="bta-add-slot-btn"
            className="flex-1 border-2 border-dashed border-[#2FA163]/60 hover:border-[#2FA163] bg-white hover:bg-[#2FA163]/5 rounded-lg py-6 text-[#286C4E] hover:text-[#092825] transition font-semibold bta-soft-pulse"
          >
            + Add another brokerage to compare ({4 - options.length} remaining)
          </button>
        </div>
      )}

      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={onBack}
          data-testid="bta-step-compare-back-btn"
          className="text-slate-600 hover:text-slate-900 font-medium"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          data-testid="bta-step-compare-next-btn"
          className="bg-[#2FA163] hover:bg-[#286C4E] text-white font-medium px-6 py-2.5 rounded shadow transition-colors"
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

function StepPipeline({ state, dispatch, onNext, onBack }) {
  return (
    <div data-testid="bta-step-pipeline">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#092825]">Your expected deals this year</h2>
          <p className="text-slate-600">Edit any row, add new deals, or start fresh.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => dispatch({ type: 'RESET_PIPELINE' })}
            data-testid="bta-reset-pipeline-btn"
            className="text-sm text-[#286C4E] hover:text-[#092825] underline"
          >
            Reset to sample pipeline
          </button>
        </div>
      </div>

      <PipelineGrid deals={state.deals} dispatch={dispatch} />

      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={onBack}
          data-testid="bta-step-pipeline-back-btn"
          className="text-slate-600 hover:text-slate-900 font-medium"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          data-testid="bta-step-pipeline-next-btn"
          className="bg-[#2FA163] hover:bg-[#286C4E] text-white font-medium px-6 py-2.5 rounded shadow transition-colors"
        >
          See results →
        </button>
      </div>
    </div>
  );
}

function StepResults({ state, onBack }) {
  const results = useMemo(
    () => state.slots.map((slot) => ({ slot, r: computeAnnualResults(slot, state.deals) })),
    [state.slots, state.deals],
  );
  const current = results.find((x) => x.slot.is_current);
  const [drillId, setDrillId] = useState(null);
  const drill = results.find((x) => x.slot.id === drillId);

  return (
    <div data-testid="bta-step-results">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#092825]">Results</h2>
          <p className="text-slate-600">Click a column header to see the full waterfall for that option.</p>
        </div>
        <button
          type="button"
          onClick={onBack}
          data-testid="bta-results-back-btn"
          className="text-sm text-slate-600 hover:text-slate-900 underline"
        >
          ← Edit pipeline
        </button>
      </div>

      <ResultsTable
        results={results}
        current={current}
        drillId={drillId}
        setDrillId={setDrillId}
      />

      {drill && <DrillDownPanel drill={drill} onClose={() => setDrillId(null)} />}

      <h3 className="text-lg font-bold text-[#092825] mb-3">Per-deal net take-home</h3>
      <PerDealMatrix results={results} deals={state.deals} />

      <p className="text-xs text-slate-500 italic px-3 -mt-4 mb-4">
        Deal net subtotal excludes monthly fees and annual E&amp;O — those come
        off the Annual net take-home above. The difference between the two rows
        equals your annual fixed costs at each option.
      </p>

      <div className="text-xs text-slate-500 p-4 bg-slate-100 rounded border border-slate-200">
        <strong>Disclaimer:</strong> Estimates are based on publicly-available, typical fee
        structures and the inputs you provide. Actual brokerage and team economics vary widely.
        Confirm with your brokerage and team before making a decision. This is a financial
        projection, not financial, legal, or tax advice.
      </div>
    </div>
  );
}

export default function BrokerTeamAnalyzerPage() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [furthest, setFurthest] = useState(0);

  const goTo = (n) => {
    dispatch({ type: 'SET_STEP', step: n });
    if (n > furthest) setFurthest(n);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const next = () => goTo(state.step + 1);
  const back = () => goTo(Math.max(0, state.step - 1));

  return (
    <MobileRedirect>
      <Helmet>
        <title>Broker & Team Analyzer · Free Tool · I Need Numbers</title>
        <meta
          name="description"
          content="Compare brokerages and team splits side by side. See your annual take-home, effective split, and cap month across up to 5 options. Free. No login."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <style>{TOOL_CSS}</style>
      <div className="bta-root" data-testid="bta-root">
        <FreeToolsNav />

        <main className="max-w-7xl mx-auto px-6 py-8">
          {state.step > 0 && <Stepper step={state.step} onGo={goTo} furthest={furthest} />}

          {state.step === 0 && <Intro onNext={next} />}
          {state.step === 1 && <StepCurrent state={state} dispatch={dispatch} onNext={next} onBack={back} />}
          {state.step === 2 && <StepCompare state={state} dispatch={dispatch} onNext={next} onBack={back} />}
          {state.step === 3 && <StepPipeline state={state} dispatch={dispatch} onNext={next} onBack={back} />}
          {state.step === 4 && <StepResults state={state} onBack={back} />}
        </main>

        <footer className="max-w-7xl mx-auto px-6 py-6 text-xs text-slate-400 text-center">
          Free tool · I Need Numbers. Defaults are typical estimates — confirm with your brokerage.
        </footer>
      </div>
    </MobileRedirect>
  );
}
